import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { VulcanIconComponent } from '@eagle-i/vulcan-component-library';
import { Store } from '@ngrx/store';
import { ApiKey } from '../../core/classes/apiKey';
import { Territory } from '../../core/classes/territory';
import { User } from '../../core/classes/user';
import { GenApiKeyRequestStatus } from '../../generated/serverModels/GenApiKeyRequestStatus';
import { GenRoleDefinition } from '../../generated/serverModels/GenRoleDefinition';
import { GenStateAccessRequest } from '../../generated/serverModels/GenStateAccessRequest';
import { LocationService } from '../../services/location.service';
import { FileDownload } from '../../shared/classes/fileDownload';
import { SNACKBAR_DURATION } from '../../shared/constants/snackbar.constants';
import {Component, computed, inject, OnInit, signal, Signal} from '@angular/core';
import {MatCardModule} from '@angular/material/card';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {AuthService} from '../../services/auth.service';
import {UserService} from '../../services/user.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import { filter, finalize, take } from 'rxjs';
import moment from 'moment';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { PhoneNumberPipe } from '../../shared/pipes/phone-number.pipe';
import { RoleNamePipe } from '../../shared/pipes/role-name.pipe';
import { selectTerritories } from '../../state/location/location.reducer';
import { ApprovedStatesModalComponent } from './modals/approved-states-modal/approved-states-modal.component';
import { NationalAccessConfirmationModalComponent } from './modals/national-access-confirmation-modal/national-access-confirmation-modal.component';
import { EditRolesModalComponent } from './modals/edit-roles-modal/edit-roles-modal.component';
import { ResetPasswordModalComponent } from './modals/reset-password-modal/reset-password-modal.component';

@Component({
  selector: 'tgrid-profile',
  imports: [
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    CommonModule,
    RoleNamePipe,
    MatTooltip,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    PhoneNumberPipe,
    VulcanIconComponent,
  ],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
})
export class ProfileComponent implements OnInit {
  private readonly excludedRoles = [
    GenRoleDefinition.ROLE_RESETTING_PASSWORD,
    GenRoleDefinition.ROLE_APPROVED_USER,
  ];

  public userService = inject(UserService);
  public authService = inject(AuthService);
  public snackBar = inject(MatSnackBar);
  public activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  public canImpersonate: boolean = true; // We will need to add logic to this when we get navigation to other profile pages

  protected readonly denialReasons: string[] = ['Invalid Account Reason'];

  protected loggedInUser = toSignal(AuthService.authenticatedUser);

  protected hasAdminControls = computed(() => {
    return this.loggedInUser()?.hasAnyRole([
      GenRoleDefinition.ROLE_ADMIN,
      GenRoleDefinition.ROLE_USER_MANAGER,
    ]);
  });

  protected profileUser = signal<User>(
    this.activatedRoute.snapshot.data['user'],
  );

  protected hasPoc = computed(() => {
    return !User.nonPocEmailExtension.some((ext) =>
      this.profileUser().email.endsWith(ext),
    );
  });

  protected visibleRoles: Signal<GenRoleDefinition[]> = computed(() => {
    return this.profileUser().roles.filter(
      (role) => !this.excludedRoles.includes(role),
    );
  });

  private locationService = inject(LocationService);
  private territories$ = inject(Store).select(selectTerritories);
  protected states = toSignal(this.territories$, { initialValue: [] });
  protected stateRequest = signal<GenStateAccessRequest[]>([]);
  protected approvedStates = signal<Territory[]>([]);
  protected hasNationalAccess = signal<boolean | null>(null);
  protected isGrantingNationalAccess = signal(false);
  protected approvedStatesList = computed(() =>
    this.approvedStates().length ? this.approvedStates().map(s => s.name).join(', ') : 'None',
  );

  constructor() {
  }

  ngOnInit(): void {
    this.loadApprovedStates();
    this.loadStateAccessRequests();
    this.loadNationalAccessStatus();
  }

  private loadApprovedStates(): void {
    this.locationService.getApprovedStates(this.profileUser().id).subscribe({
      next: (states) => this.approvedStates.set(states),
    });
  }
  private loadNationalAccessStatus(): void {
    this.locationService.hasNationalAccess(this.profileUser().id).subscribe({
      next: (hasNationalAccess) => this.hasNationalAccess.set(hasNationalAccess),
      error: () => this.hasNationalAccess.set(false),
    });
  }

  private loadStateAccessRequests(): void {
    this.locationService.getStateAccessRequests(this.profileUser().id)
      .subscribe({
        next: (requests) => this.stateRequest.set(requests.filter(r => r.status === GenApiKeyRequestStatus.PENDING)),
      });
  }

  public enableUser(): void {
    this.userService.enableUser(this.profileUser()).subscribe({
      next: (user) => {
        this.snackBar.open('User Enabled Successfully', '', {
          duration: SNACKBAR_DURATION.NORMAL,
          panelClass: ['success'],
        });
        this.profileUser.update((currentUser) => {
          user.pointOfContact = currentUser.pointOfContact;
          return user;
        });
      },
      error: (error: string) => {
        console.error(error);
        this.snackBar.open('User Enable Failed', 'OK', {
          panelClass: ['failure'],
        });
      },
    });
  }

  public disableUser(): void {
    this.userService.disableUser(this.profileUser()).subscribe({
      next: (user) => {
        this.profileUser.update((currentUser) => {
          user.pointOfContact = currentUser.pointOfContact;
          return user;
        });
        this.snackBar.open('User Disabled Successfully', '', {
          duration: SNACKBAR_DURATION.NORMAL,
          panelClass: ['success'],
        });
      },
      error: (error: string) => {
        console.error(error);
        this.snackBar.open('User Disable Failed', 'OK', {
          panelClass: ['failure'],
        });
      },
    });
  }

  protected openPasswordResetModal(): void {
    const config = {
      autoFocus: false,
      disableClose: true,
      data: {
        userId: this.profileUser().id,
      },
    };

    this.dialog
      .open(ResetPasswordModalComponent, config)
      .afterClosed()
      .pipe(filter((success) => !!success))
      .subscribe({
        next: () => {
          this.profileUser().lastPasswordReset = moment();
        },
      });
  }

  protected impersonateUser(): void {
    this.authService
      .impersonateUser(this.profileUser())
      .pipe(take(1))
      .subscribe({
        next: (impersonatedUser: User) => {
          this.snackBar.open(
            `Impersonating ${impersonatedUser.firstName} ${impersonatedUser.lastName}, Refreshing Page....`,
            '',
            { duration: SNACKBAR_DURATION.NORMAL, panelClass: 'success' },
          );
          this.router
            .navigateByUrl('/app/landing')
            .then(() => window.location.reload());
        },
        error: (error) => {
          this.snackBar.open('Impersonation Failed', '', {
            duration: SNACKBAR_DURATION.NORMAL, panelClass: 'failure',
          });
          console.error(error);
        },
      });
  }

  protected sendPasswordResetEmail(): void {
    this.userService
      .sendPasswordResetEmail(this.profileUser().username)
      .subscribe({
        next: () => {
          this.snackBar.open('Sent password reset email', 'X', {
            duration: SNACKBAR_DURATION.MEDIUM,
          });
        },
        error: (error: HttpErrorResponse) => {
          console.error(error);
          this.snackBar.open(
            'There was an error sending the password reset email',
            'X',
            { duration: SNACKBAR_DURATION.MEDIUM },
          );
        },
      });
  }

  protected openEditRoleModal() {
    const config = {
      autoFocus: false,
      disableClose: true,
      data: {
        user: this.profileUser(),
      },
    };

    this.dialog
      .open(EditRolesModalComponent, config)
      .afterClosed()
      .pipe(filter((success) => !!success))
      .subscribe({
        next: (newRoles: GenRoleDefinition[]) => {
          this.profileUser.update(user => {
            user.roles = newRoles;
            return user;
          });
        },
      });
  }

  protected approveUser(): void {
    this.userService.approveUser(this.profileUser()).subscribe((updatedUser) => {
      this.profileUser.update(() => updatedUser);
      this.openApprovedStatesModal();
      this.snackBar.open(
        `${this.profileUser().fullName} has been approved`,
        'X',
        { duration: SNACKBAR_DURATION.MEDIUM },
      );
    });
  }

  protected denyUser(reason: string): void {
    this.userService.denyUser(this.profileUser(), reason).subscribe(() => {
      this.snackBar.open(
        `${this.profileUser().fullName} has been denied`,
        'X',
        { duration: SNACKBAR_DURATION.MEDIUM },
      );
    });
  }

  openApprovedStatesModal() {
    const config: MatDialogConfig = {
      disableClose: true,
      width: '400px',
      data: {
        userId: this.profileUser().id,
      },
    };
    this.dialog
      .open(ApprovedStatesModalComponent, config)
      .afterClosed()
      .pipe(filter((result) => !!result))
      .subscribe({
        next: () => {
          this.loadApprovedStates();
          this.snackBar.open('Successfully set approved states', 'X', {
            duration: SNACKBAR_DURATION.LONG,
          });
        },
        error: (e) => {
          this.snackBar.open('Failed to set approved states', 'X', {
            duration: SNACKBAR_DURATION.LONG,
          });
          console.error(e);
        },
      });
  }

  protected openNationalAccessConfirmation(): void {
    this.dialog
      .open(NationalAccessConfirmationModalComponent, {
        data: { userName: this.profileUser().fullName },
      })
      .afterClosed()
      .pipe(filter((confirmed) => confirmed === true))
      .subscribe(() => this.grantNationalAccess());
  }

  private grantNationalAccess(): void {
    this.isGrantingNationalAccess.set(true);
    this.locationService
      .grantNationalAccess(this.profileUser().id)
      .pipe(finalize(() => this.isGrantingNationalAccess.set(false)))
      .subscribe({
        next: () => {
          this.hasNationalAccess.set(true);
          this.stateRequest.set([]);
          this.loadApprovedStates();
          this.snackBar.open('National access granted successfully', 'X', {
            duration: SNACKBAR_DURATION.LONG,
            panelClass: ['success'],
          });
        },
        error: (error: HttpErrorResponse) => {
          console.error(error);
          this.snackBar.open('Failed to grant national access', 'X', {
            duration: SNACKBAR_DURATION.LONG,
            panelClass: ['failure'],
          });
        },
      });
  }

  protected copyApiKey(key: string) {
    FileDownload.copyTextToClipBoard(key).subscribe({
      next: () => {
        this.snackBar.open('API key copied', 'X', { duration: 3_000 });
      }, error: () => {
        this.snackBar.open('Failed to copy API key', 'X', { duration: 3_000 });
      },
    });
  }


  protected renewApiKey(apiKey: ApiKey) {
    const updatedKey = new ApiKey(apiKey);
    updatedKey.expiresAt = moment().add(1, 'year');

    this.userService.updateApiKey(updatedKey.id, updatedKey).subscribe({
      next: (response) => {
        this.profileUser.update(user => {
          const index = user.apiKeys
            .map(keys => keys.id)
            .indexOf(updatedKey.id);

          if (index !== -1) {
            user.apiKeys[index] = response;
          }
          return user;
        });


        this.snackBar.open('API key renewed', 'X', { duration: 3_000 });
      },
      error: () => {
        this.snackBar.open('Failed to renew API key', 'X', { duration: 3_000 });
      },
    });
  }
}
